import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ApiClientError, type MessageView, type UserView } from '@olmt/shared'
import { createPoll, saveEditedMessage, sendMessage, uploadAndSendMedia, uploadVoiceNote } from '../lib/data'
import { insertMention, mentionCandidates, mentionQuery } from '../lib/mentions'
import { sendTyping } from '../lib/socket'
import { t, useT } from '../lib/i18n'
import { messagePreview } from '../lib/chat'
import { formatDuration } from '../lib/time'
import { useVoiceRecorder } from '../hooks/useVoiceRecorder'
import { IconButton } from './IconButton'
import { PollDialog } from './PollDialog'
import {
  CheckIcon,
  CloseIcon,
  FileIcon,
  ImageIcon,
  MicIcon,
  PaperclipIcon,
  PollIcon,
  SendIcon,
  TrashIcon,
  VideoIcon,
} from './Icons'
import { Spinner } from './Spinner'

/**
 * Upload failures the user can act on get a specific message. `storage_full` is a real state on a
 * host whose data directory shares a volume: the api refuses the write rather than filling it.
 */
function uploadErrorMessage(caught: unknown, name: string): string {
  const code = caught instanceof ApiClientError ? caught.code : undefined
  if (code === 'storage_full') return t('composer.storageFull')
  if (code === 'too_large') return t('composer.tooLarge', { name })
  return t('composer.uploadFailed', { name })
}

type ComposerProps = {
  chatId: string
  /**
   * The chat's members (W10): the `@` picker offers them, and their handles are what a body has to
   * spell for the send to name them.
   */
  members: UserView[]
  replyTo: MessageView | null
  onCancelReply: () => void
  /** The message being rewritten in place (W2); null is the normal compose state. */
  editing: MessageView | null
  onCancelEdit: () => void
}

type UploadState = { name: string; progress: number }

const ATTACHMENTS = [
  { label: 'composer.attachPhoto', Icon: ImageIcon },
  { label: 'composer.attachVideo', Icon: VideoIcon },
  { label: 'composer.attachFile', Icon: FileIcon },
] as const

/** The keydowns that move the caret without typing; each can land back inside a half-typed handle. */
const CARET_KEYS: Record<string, true> = {
  ArrowLeft: true,
  ArrowRight: true,
  ArrowUp: true,
  ArrowDown: true,
  Home: true,
  End: true,
}

/**
 * The ids that join the field to the picker it opened: the field's `aria-controls` and
 * `aria-activedescendant` point at the list and at the row the arrows are on. One prefix, so the
 * three of them cannot drift apart.
 */
const MENTION_LIST_ID = 'composer-mention-list'
const MENTION_LABEL_ID = 'composer-mention-label'
const MENTION_OPTION_PREFIX = 'composer-mention-option-'

export function Composer({ chatId, members, replyTo, onCancelReply, editing, onCancelEdit }: ComposerProps) {
  const t = useT()
  const [text, setText] = useState('')
  const [attachOpen, setAttachOpen] = useState(false)
  /** W11: the create-poll dialog is up. It owns its draft; the composer only opens and closes it. */
  const [pollOpen, setPollOpen] = useState(false)
  const [upload, setUpload] = useState<UploadState | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * W10 — the open `@` picker: the handle under the caret, and which row the arrows are on. The
   * *query* is state because the caret is the browser's, not React's; every other row of the picker
   * is derived from it and from the chat's members on each render, never remembered.
   */
  const [mention, setMention] = useState<{ query: string; index: number } | null>(null)
  /** The caret the field last reported, which is what an insertion replaces. */
  const caretRef = useRef(0)
  /** Where the caret belongs once the text an insertion produced has rendered. */
  const caretAfterInsert = useRef<number | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mentionListRef = useRef<HTMLDivElement>(null)
  const imageInput = useRef<HTMLInputElement>(null)
  const videoInput = useRef<HTMLInputElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const recorder = useVoiceRecorder()

  const query = mention?.query ?? null
  const candidates = useMemo(
    () => (query === null ? [] : mentionCandidates(members, query)),
    [members, query],
  )
  // A query that lost members (a letter typed) can leave the stored index past the list; the
  // highlighted row is clamped, so Enter always takes a row that exists.
  const highlighted =
    candidates.length === 0 ? -1 : Math.min(mention?.index ?? 0, candidates.length - 1)

  useEffect(() => {
    const element = textareaRef.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${Math.min(element.scrollHeight, 160)}px`
  }, [text])

  useLayoutEffect(() => {
    const caret = caretAfterInsert.current
    if (caret === null) return
    caretAfterInsert.current = null
    const element = textareaRef.current
    if (!element) return
    // The field keeps the focus, and with it the phone's keyboard: picking a name is a step in
    // typing the message, not a detour out of it.
    element.focus()
    element.setSelectionRange(caret, caret)
    caretRef.current = caret
  }, [text])

  // The highlighted row has to be on screen, or the arrows navigate a list the writer cannot see
  // and Enter takes a row they never read.
  useEffect(() => {
    if (highlighted < 0) return
    mentionListRef.current
      ?.querySelector('[role="option"][aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  useEffect(() => {
    setText('')
    setError(null)
    setAttachOpen(false)
    setPollOpen(false)
    setMention(null)
  }, [chatId])

  // Edit mode is seeded once per message, keyed on the id — not the view: an edit frame can
  // replace the stored object while the user is typing, and re-seeding on every replacement would
  // throw the keystrokes away. Leaving edit mode puts the field back to its normal empty state:
  // the draft belonged to the edit, and on a delete it belongs to a message that is gone.
  useEffect(() => {
    if (editing) {
      setText(editing.body ?? '')
      textareaRef.current?.focus()
    } else {
      setText('')
    }
    setError(null)
    setMention(null)
  }, [editing?.id])

  const busy = upload !== null

  const cancelEdit = () => {
    setError(null)
    onCancelEdit()
  }

  /**
   * Reads the field's caret and opens, keeps or closes the picker (W10). The query is what decides:
   * a caret still inside a handle keeps the same list (and the row the arrows reached), a caret
   * outside one closes it.
   */
  const syncMention = (element: HTMLTextAreaElement) => {
    const caret = element.selectionStart ?? element.value.length
    caretRef.current = caret
    const next = mentionQuery(element.value, caret)
    setMention((open) =>
      next === null ? null : open !== null && open.query === next ? open : { query: next, index: 0 },
    )
  }

  const moveMention = (step: number) => {
    setMention((open) => {
      if (open === null || candidates.length === 0) return open
      const current = Math.min(open.index, candidates.length - 1)
      return { query: open.query, index: (current + step + candidates.length) % candidates.length }
    })
  }

  /** A row was taken: the half-typed handle becomes `@handle `, and the picker's work is done. */
  const chooseMention = (member: UserView) => {
    const inserted = insertMention(text, caretRef.current, member.handle)
    caretAfterInsert.current = inserted.caret
    setText(inserted.text)
    setMention(null)
    if (!editing) sendTyping(chatId)
  }

  const submitText = async () => {
    const body = text.trim()
    // A blank save is refused locally: the api answers a blank `PATCH` with a 400, and a round
    // trip that can only fail is not worth the flicker.
    if (!body || busy) return
    if (editing) {
      setError(null)
      try {
        // W10: an edit carries no `mentions` — the frozen `PATCH` has no such field. A handle added
        // here is still lit from the body, but only a new message can name somebody out loud.
        await saveEditedMessage(editing.id, body)
        onCancelEdit()
      } catch {
        // Edit mode stays up with the draft in the field, so the save is one press away again.
        setError(t('chat.editFailed'))
      }
      return
    }
    setError(null)
    setText('')
    setMention(null)
    const replyToId = replyTo?.id ?? null
    try {
      // W10: whoever the body names is read off it inside `sendMessage` — the picker leaves no set
      // behind for a backspaced handle to survive in.
      await sendMessage({ chatId, kind: 'text', body, replyToId })
      onCancelReply()
    } catch {
      setText(body)
      setError(t('composer.messageNotSent'))
    }
  }

  const sendFile = async (file: File) => {
    setError(null)
    setAttachOpen(false)
    setUpload({ name: file.name, progress: 0 })
    const kind = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
        ? 'video'
        : file.type.startsWith('audio/')
          ? 'voice'
          : 'file'
    try {
      await uploadAndSendMedia({
        chatId,
        file,
        kind,
        replyToId: replyTo?.id ?? null,
        onProgress: (progress) => setUpload({ name: file.name, progress }),
      })
      onCancelReply()
    } catch (caught) {
      setError(uploadErrorMessage(caught, file.name))
    } finally {
      setUpload(null)
    }
  }

  const finishRecording = async () => {
    const clip = await recorder.stop()
    if (!clip) return
    setError(null)
    setUpload({ name: t('composer.voiceMessage'), progress: 0 })
    try {
      await uploadVoiceNote({
        chatId,
        blob: clip.blob,
        filename: clip.filename,
        replyToId: replyTo?.id ?? null,
        onProgress: (progress) => setUpload({ name: t('composer.voiceMessage'), progress }),
      })
      onCancelReply()
    } catch (caught) {
      setError(uploadErrorMessage(caught, t('composer.voiceMessage')))
    } finally {
      setUpload(null)
    }
  }

  const inputRefs = [imageInput, videoInput, fileInput]

  return (
    <div className="composer border-t border-line bg-surface">
      <div className="room-column">
        {upload && (
          <div className="px-3 pt-2">
            <p className="flex items-center gap-2 text-meta text-muted">
              <Spinner size={12} label={t('composer.uploading')} />
              <span className="min-w-0 flex-1 truncate">
                {t('composer.uploading')} <bdi>{upload.name}</bdi>
              </span>
              <span className="numeric">{Math.round(upload.progress * 100)}%</span>
            </p>
            <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-surface2">
              <span
                className="block h-full rounded-full bg-accent transition-[width] duration-quick"
                style={{ width: `${Math.round(upload.progress * 100)}%` }}
              />
            </span>
          </div>
        )}

        {editing && (
          <div className="mx-3 mt-2 flex items-center gap-2 rounded-panel border-s-2 border-accent bg-surface2 px-3 py-2 ring-1 ring-line ring-inset">
            <span className="min-w-0 flex-1">
              <span className="block text-meta font-semibold text-accent">{t('chat.editing')}</span>
              <span className="block truncate text-meta text-muted">
                <bdi>{messagePreview(editing, null)}</bdi>
              </span>
            </span>
            <IconButton label={t('chat.cancelEdit')} size={32} onClick={cancelEdit}>
              <CloseIcon size={16} />
            </IconButton>
          </div>
        )}

        {replyTo && !editing && (
          <div className="mx-3 mt-2 flex items-center gap-2 rounded-panel border-s-2 border-accent bg-surface2 px-3 py-2 ring-1 ring-line ring-inset">
            <span className="min-w-0 flex-1">
              <span className="block text-meta font-semibold text-accent">{t('composer.reply')}</span>
              <span className="block truncate text-meta text-muted">
                <bdi>{messagePreview(replyTo, null)}</bdi>
              </span>
            </span>
            <IconButton label={t('composer.cancelReply')} size={32} onClick={onCancelReply}>
              <CloseIcon size={16} />
            </IconButton>
          </div>
        )}

        {error && (
          <p role="alert" className="px-3 pt-2 text-meta text-danger">
            {error}
          </p>
        )}

        {attachOpen && (
          <div className="flex flex-wrap gap-2 px-3 pt-2" role="group" aria-label={t('composer.attach')}>
            {ATTACHMENTS.map(({ label, Icon }, index) => (
              <button
                key={label}
                type="button"
                onClick={() => inputRefs[index]?.current?.click()}
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-surface2 px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface3"
              >
                <Icon size={18} />
                {t(label)}
              </button>
            ))}
            {/* W11 — the one attach surface that is not an upload: a poll is written here, not
                picked from the device, so it does not belong with the three hidden file inputs
                above; it opens the dialog and the composer is done with it. */}
            <button
              type="button"
              onClick={() => {
                setAttachOpen(false)
                setPollOpen(true)
              }}
              className="inline-flex min-h-11 items-center gap-2 rounded-full bg-surface2 px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface3"
            >
              <PollIcon size={18} />
              {t('poll.title')}
            </button>
          </div>
        )}

        {recorder.status === 'recording' ? (
          <div className="flex items-center gap-2 px-3 pt-2">
            <IconButton label={t('composer.cancelRecording')} variant="danger" size={40} onClick={recorder.cancel}>
              <TrashIcon size={20} />
            </IconButton>
            <span className="flex min-h-11 flex-1 items-center gap-3 rounded-well bg-surface2 px-4 ring-1 ring-danger/40 ring-inset">
              <span aria-hidden="true" className="flex h-5 items-end gap-0.5">
                {[0, 1, 2, 3, 4].map((index) => (
                  <span
                    key={index}
                    className="w-1 rounded-full bg-danger"
                    style={{ height: `${Math.max(3, Math.min(20, recorder.level * 20 * (1 - index * 0.12)))}px` }}
                  />
                ))}
              </span>
              <span className="numeric text-ui" aria-live="off">
                {formatDuration(recorder.seconds * 1000)}
              </span>
              <span className="flex-1 text-meta text-muted">{t('composer.recording')}</span>
            </span>
            <IconButton label={t('composer.sendVoiceMessage')} variant="accent" size={40} onClick={() => void finishRecording()}>
              <SendIcon size={20} />
            </IconButton>
          </div>
        ) : (
          <div className="flex items-end gap-2 px-3 pt-2">
            <IconButton
              label={attachOpen ? t('composer.closeAttachments') : t('composer.attach')}
              pressed={attachOpen}
              onClick={() => setAttachOpen((open) => !open)}
            >
              {attachOpen ? <CloseIcon size={22} /> : <PaperclipIcon size={22} />}
            </IconButton>

            {/* The field's own column is what the picker is anchored to (W10): the list hangs
                above the well, over the thread — never under a thumb, never over the message being
                typed. */}
            <div className="relative min-w-0 flex-1">
              {mention !== null && (
                <div className="panel-enter absolute inset-x-0 bottom-full z-20 mb-2 overflow-hidden rounded-panel bg-surface shadow-lift ring-1 ring-line-strong">
                  <p
                    id={MENTION_LABEL_ID}
                    className="border-b border-line ps-3.5 pe-3 py-1.5 text-meta font-semibold text-accent"
                  >
                    {t('chat.mentionsTitle')}
                  </p>
                  {candidates.length === 0 ? (
                    <p className="ps-3.5 pe-3 py-2.5 text-ui text-muted">{t('chat.mentionsEmpty')}</p>
                  ) : (
                    /* A listbox the field drives: the focus stays in the textarea and its
                       `aria-activedescendant` is what points at the row the arrows are on, so a
                       keyboard (and a phone keyboard, which has no Tab) reaches every row without
                       the field ever losing the writer's place. */
                    <div
                      ref={mentionListRef}
                      id={MENTION_LIST_ID}
                      role="listbox"
                      aria-labelledby={MENTION_LABEL_ID}
                      className="max-h-56 overflow-y-auto py-1"
                    >
                      {candidates.map((member, index) => (
                        <div
                          key={member.id}
                          id={`${MENTION_OPTION_PREFIX}${member.id}`}
                          role="option"
                          aria-selected={index === highlighted}
                          aria-label={t('chat.mentionInsert', { handle: `@${member.handle}` })}
                          onClick={() => chooseMention(member)}
                          className={`flex min-h-11 cursor-pointer items-center gap-2 border-s-2 px-3 py-1.5 transition-colors duration-quick ${
                            index === highlighted ? 'border-accent bg-surface2' : 'border-transparent'
                          }`}
                        >
                          <span className="min-w-0 flex-1 truncate text-ui text-ink">
                            <bdi>{member.displayName}</bdi>
                          </span>
                          {/* The handle is Latin in every chrome (docs/DESIGN.md §14), so it is an
                              island inside an Arabic list rather than a run the RTL order moves. */}
                          <span className="text-meta text-muted">
                            <bdi>@{member.handle}</bdi>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex min-h-11 min-w-0 w-full items-end rounded-well bg-surface2 ring-1 ring-line ring-inset transition-colors duration-quick focus-within:ring-accent/70">
                <textarea
                  ref={textareaRef}
                  value={text}
                  rows={1}
                  placeholder={t('composer.placeholder')}
                  aria-label={t('composer.placeholder')}
                  aria-expanded={mention !== null}
                  aria-controls={candidates.length === 0 ? undefined : MENTION_LIST_ID}
                  aria-activedescendant={
                    highlighted < 0 ? undefined : `${MENTION_OPTION_PREFIX}${candidates[highlighted].id}`
                  }
                  enterKeyHint="send"
                  onChange={(event) => {
                    setText(event.target.value)
                    syncMention(event.target)
                    // Typing while editing is a rewrite, not a new message: the peer's indicator
                    // would be a lie.
                    if (!editing) sendTyping(chatId)
                  }}
                  onSelect={(event) => syncMention(event.currentTarget)}
                  onKeyUp={(event) => {
                    // A caret that moved without typing can land back inside a half-typed handle,
                    // and the picker follows it. The arrows belong to the picker while it is open,
                    // which the keydown below has already claimed.
                    if (mention !== null || CARET_KEYS[event.key] !== true) return
                    syncMention(event.currentTarget)
                  }}
                  onKeyDown={(event) => {
                    if (mention !== null) {
                      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault()
                        moveMention(event.key === 'ArrowDown' ? 1 : -1)
                        return
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault()
                        setMention(null)
                        return
                      }
                      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                        const pick = candidates[highlighted]
                        // A query nobody matches leaves Enter its ordinary meaning: the message is
                        // sent with the `@…` text as typed.
                        if (pick) {
                          event.preventDefault()
                          chooseMention(pick)
                          return
                        }
                      }
                    }
                    if (event.key === 'Escape' && editing) {
                      event.preventDefault()
                      cancelEdit()
                      return
                    }
                    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
                    event.preventDefault()
                    void submitText()
                  }}
                  className="max-h-40 min-h-11 w-full resize-none bg-transparent px-4 py-2.5 text-body text-ink placeholder:text-muted"
                />
              </div>
            </div>

            {editing ? (
              // Edit mode's action is the save, and only the save: a blank body is refused here
              // exactly as the api would refuse it (400), and the mic has no meaning mid-edit.
              <IconButton
                label={t('chat.saveEdit')}
                variant="accent"
                disabled={busy || text.trim().length === 0}
                onClick={() => void submitText()}
              >
                <CheckIcon size={20} />
              </IconButton>
            ) : text.trim().length > 0 ? (
              <IconButton label={t('composer.send')} variant="accent" disabled={busy} onClick={() => void submitText()}>
                <SendIcon size={20} />
              </IconButton>
            ) : (
              <IconButton
                label={t('composer.record')}
                disabled={busy || recorder.status === 'denied'}
                onClick={() => void recorder.start()}
              >
                <MicIcon size={22} />
              </IconButton>
            )}
          </div>
        )}

        {recorder.status === 'denied' && (
          <p role="alert" className="px-3 pb-1 text-meta text-danger">
            {t('composer.micBlocked')}
          </p>
        )}
      </div>

      <input
        ref={imageInput}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void sendFile(file)
        }}
      />
      <input
        ref={videoInput}
        type="file"
        accept="video/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void sendFile(file)
        }}
      />
      <input
        ref={fileInput}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void sendFile(file)
        }}
      />

      {/* W11 — the create-poll dialog, over the thread like every other sheet in the app. It keeps
          its own draft and closes only when the poll is stored; the reply chip goes with the send,
          the way it does for any other message the composer sends. */}
      {pollOpen && (
        <PollDialog
          onClose={() => setPollOpen(false)}
          onSend={async (draft) => {
            await createPoll(chatId, draft)
            setPollOpen(false)
            onCancelReply()
          }}
        />
      )}
    </div>
  )
}
